import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { MatchRepository } from '@data/match/match.store';
import {
  combineLatest,
  combineLatestWith,
  defaultIfEmpty,
  filter,
  Observable,
  of,
  startWith,
  switchMap,
  take,
} from 'rxjs';
import { UserRepository } from '@data/user/user.store';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { FlexModule } from '@ngbracket/ngx-layout';
import { MatButton } from '@angular/material/button';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatBadge } from '@angular/material/badge';
import { ExtendedModule } from '@ngbracket/ngx-layout/extended';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { APP_CONFIG } from '@core/config/app-config.token';
import { isAnymatch } from '@core/config/brand';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map } from 'rxjs/operators';
import { SpotlightOverlayComponent } from '@shared/components/spotlight-overlay/spotlight-overlay.component';
import { MASCOT_DIALOG_PRIORITY } from '@shared/components/mascot-dialog/mascot-dialog-orchestrator.service';
import { FRAME_MATCHES } from '@core/services/fair-tabs/fair-tabs.service';

@UntilDestroy()
@Component({
  selector: 'recrewt-matches-toolbar-layout',
  templateUrl: './matches-toolbar-layout.component.html',
  styleUrls: ['./matches-toolbar-layout.component.scss'],
  imports: [
    MatToolbar,
    MatToolbarRow,
    FlexModule,
    MatButton,
    RouterLink,
    RouterLinkActive,
    MatIcon,
    MatBadge,
    ExtendedModule,
    RouterOutlet,
    AsyncPipe,
    TranslateModule,
    SpotlightOverlayComponent,
  ],
})
export class MatchesToolbarLayoutComponent implements OnInit {
  protected readonly mascotPriority = MASCOT_DIALOG_PRIORITY;

  static readonly LAST_TAB_KEY = 'matchesToolbarLastTab';
  private static readonly VALID_TABS = ['/matching/rate', '/matching/rated'];
  private static readonly SPOTLIGHT_SHOWN_KEY = 'ratedTabSpotlightShown';

  private appConfig = inject(APP_CONFIG);
  private readonly router = inject(Router);

  readonly showRoadmapDownload = this.appConfig.modules.browser.allowMarking;

  /** anymatch lists its matches instead of rating them, so it has no rate/rated tabs. */
  readonly stopMode = isAnymatch(this.appConfig);

  /** The job app embedded in its fair's anymatch app has its tabs in the app bar (toolbar-layout). */
  readonly embedded = !!this.appConfig.app.embeddedIn;

  loading = false;

  ratedMatchesCount$: Observable<number>;

  unratedMatchesCount$: Observable<number>;

  showRatedTabPulse$: Observable<boolean>;

  show = false;

  showSpotlight = false;

  @ViewChild('ratedTabBtn', { read: ElementRef }) ratedTabBtn!: ElementRef<HTMLElement>;

  constructor() {
    const matchesRepo = inject(MatchRepository);
    const userRepo = inject(UserRepository);

    // the fair's app may point out its own tab once the job app's matches show (CREW-1488)
    if (this.embedded) {
      window.parent.postMessage(
        { type: FRAME_MATCHES },
        new URL(this.appConfig.app.embeddedIn!).origin,
      );
    }

    this.ratedMatchesCount$ = matchesRepo.ratedMatchesAmount$.pipe(defaultIfEmpty(0));
    this.unratedMatchesCount$ = matchesRepo.unratedMatchesAmount$.pipe(defaultIfEmpty(0));

    this.showRatedTabPulse$ = combineLatest([
      this.ratedMatchesCount$,
      this.router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map((e) => e.urlAfterRedirects),
        startWith(this.router.url),
      ),
    ]).pipe(map(([count, url]) => count > 0 && !url.startsWith('/matching/rated')));

    this.ratedMatchesCount$
      .pipe(
        filter((count) => count > 0),
        untilDestroyed(this),
        take(1),
      )
      .subscribe(() => {
        if (!sessionStorage.getItem(MatchesToolbarLayoutComponent.SPOTLIGHT_SHOWN_KEY)) {
          this.showSpotlight = true;
        }
      });

    this.ratedMatchesCount$
      .pipe(combineLatestWith(this.unratedMatchesCount$))
      .pipe(
        switchMap(([a, b]) =>
          a + b === 0 ? matchesRepo.loadForSaveState(userRepo.selectedSaveStateRef!) : of(),
        ),
        untilDestroyed(this),
      )
      .subscribe();
  }

  ngOnInit(): void {
    if (this.stopMode) {
      this.show = true;
      return;
    }
    this.restoreLastActiveTab();
    this.trackActiveTab();
  }

  private restoreLastActiveTab(): void {
    const lastTab = sessionStorage.getItem(MatchesToolbarLayoutComponent.LAST_TAB_KEY);
    if (
      lastTab &&
      MatchesToolbarLayoutComponent.VALID_TABS.includes(lastTab) &&
      !this.router.url.startsWith(lastTab)
    ) {
      void this.router.navigateByUrl(lastTab).then(() => (this.show = true));
    } else {
      this.show = true;
    }
  }

  private trackActiveTab(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map((event) =>
          MatchesToolbarLayoutComponent.VALID_TABS.find((tab) => event.urlAfterRedirects === tab),
        ),
        filter(Boolean),
        untilDestroyed(this),
      )
      .subscribe((matchedTab) =>
        sessionStorage.setItem(MatchesToolbarLayoutComponent.LAST_TAB_KEY, matchedTab),
      );
  }

  onSpotlightDismiss(): void {
    sessionStorage.setItem(MatchesToolbarLayoutComponent.SPOTLIGHT_SHOWN_KEY, 'true');
    this.showSpotlight = false;
  }
}
